import { Input } from "@groot/ui/input";
import { Page } from "@/app/shared/components/page";
import { PageHeader } from "@groot/shell/components/layout";
import { getBookmarkableItems } from "@/config/navigation";
import type { NavigationItem } from "@/config/navigation";
import { Search } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

export default function Bookmarks() {
  const [searchQuery, setSearchQuery] = useState("");
  const allItems = getBookmarkableItems();

  // Filter items based on search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return allItems;
    }

    const query = searchQuery.toLowerCase();
    return allItems.filter((item) => {
      // Search in label
      if (item.label.toLowerCase().includes(query)) {
        return true;
      }

      // Search in keywords
      if (item.keywords?.some((keyword) => keyword.toLowerCase().includes(query))) {
        return true;
      }

      return false;
    });
  }, [searchQuery, allItems]);

  // Group items by category
  const groupedItems = useMemo(() => {
    const groups: { title: string; items: NavigationItem[] }[] = [
      {
        title: "Features",
        items: filteredItems.filter((item) => !item.isSecondary),
      },
      {
        title: "Tools & Settings",
        items: filteredItems.filter((item) => item.isSecondary),
      },
    ];

    // Filter out empty groups
    return groups.filter((group) => group.items.length > 0);
  }, [filteredItems]);

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Bookmarks"
        description="Quick access to all features and tools in one place"
        className="mb-8"
      />

      {/* Search Bar */}
      <div className="mb-8">
        <div className="relative max-w-2xl">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-muted-foreground/70" />
          <Input
            type="text"
            placeholder="Search features, tools, or settings..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 h-12 text-base border-input"
          />
        </div>
      </div>

      {/* Results Count */}
      {searchQuery && (
        <div className="mb-6">
          <p className="text-sm text-muted-foreground">
            {filteredItems.length === 0
              ? "No features found"
              : filteredItems.length === 1
                ? "1 feature found"
                : `${filteredItems.length} features found`}
          </p>
        </div>
      )}

      {/* No Results Message */}
      {searchQuery && filteredItems.length === 0 && (
        <div className="border border-dashed border-input rounded-lg">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Search className="size-8 text-muted-foreground/70" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No features found</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              Try searching with different keywords or browse all features below.
            </p>
          </div>
        </div>
      )}

      {/* Grouped Feature Cards */}
      {groupedItems.map((group) => (
        <div key={group.title} className="mb-12 last:mb-0">
          <h2 className="text-lg font-medium text-foreground mb-6">{group.title}</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {group.items.map((item) => (
              <Link key={item.id} to={item.path}>
                <div className="h-full border border-border rounded-lg p-6 hover:border-input hover:bg-muted cursor-pointer group">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-muted flex items-center justify-center group-hover:bg-accent">
                      <item.icon className="size-6 text-foreground/80" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-base text-foreground mb-1">{item.label}</h3>
                      {item.keywords && item.keywords.length > 0 && (
                        <p className="text-xs text-muted-foreground line-clamp-2">
                          {item.keywords.slice(0, 3).join(" • ")}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      ))}

      {/* Empty State - No items at all */}
      {!searchQuery && allItems.length === 0 && (
        <div className="border border-dashed border-input rounded-lg">
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Search className="size-8 text-muted-foreground/70" />
            </div>
            <h3 className="text-lg font-medium text-foreground mb-2">No features available</h3>
            <p className="text-sm text-muted-foreground text-center max-w-md">
              All features are currently in the main navigation.
            </p>
          </div>
        </div>
      )}
    </Page>
  );
}
